/**
 * API 통신 클라이언트
 * 백엔드 API와의 통신을 담당합니다.
 */

/** API 기본 URL (현재 오리진 기준) */
const BASE_URL = '';

/** API 응답 타입 */
interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * 인증 토큰 관리
 */
let _cachedToken: string | null = null;

export function getToken(): string | null {
  if (_cachedToken === null) {
    _cachedToken = localStorage.getItem('kalpha_token');
  }
  return _cachedToken;
}

export function setToken(token: string): void {
  _cachedToken = token;
  localStorage.setItem('kalpha_token', token);
}

export function removeToken(): void {
  _cachedToken = null;
  localStorage.removeItem('kalpha_token');
}

/**
 * API 요청 래퍼
 * - fetch/JSON 파싱 에러 처리
 * - HTTP 상태 코드 체크
 * - GET 요청 재시도 1회
 */
async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  auth: boolean = true,
  extraHeaders?: Record<string, string>
): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...extraHeaders,
  };

  if (auth) {
    const token = getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const url = `${BASE_URL}${path}`;
  const doFetch = async (): Promise<ApiResponse<T>> => {
    try {
      const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });

      if (!response.ok) {
        // JSON 파싱 시도
        try {
          const errBody = await response.json();
          return { success: false, error: errBody.error || `HTTP ${response.status}` };
        } catch {
          return { success: false, error: `HTTP ${response.status} ${response.statusText}` };
        }
      }

      return await response.json();
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Network error' };
    }
  };

  const result = await doFetch();
  // GET 요청 실패 시 1회 재시도
  if (!result.success && method === 'GET' && result.error?.includes('Network')) {
    await new Promise(r => setTimeout(r, 500));
    return doFetch();
  }
  return result;
}

// ---- 인증 API ----

export async function apiRegister(username: string, password: string, displayName: string, tsToken?: string) {
  return request<{ user_id: string; username: string; display_name: string; token: string }>(
    'POST', '/api/auth/register', { username, password, display_name: displayName }, false,
    tsToken ? { 'X-Turnstile-Token': tsToken } : undefined
  );
}

export async function apiLogin(username: string, password: string, tsToken?: string) {
  return request<{ user_id: string; username: string; display_name: string; token: string }>(
    'POST', '/api/auth/login', { username, password }, false,
    tsToken ? { 'X-Turnstile-Token': tsToken } : undefined
  );
}

export async function apiLogout() {
  return request('POST', '/api/auth/logout');
}

export async function apiGetMe() {
  return request<{ user_id: string; username: string; display_name: string; avatar_color: string }>(
    'GET', '/api/auth/me'
  );
}

// ---- 채팅방 API ----

export async function apiGetRooms() {
  return request<Array<{
    id: string;
    name: string;
    created_by: string;
    member_count: number;
    last_message?: string;
    last_message_time?: number;
    has_password?: number;
  }>>('GET', '/api/rooms');
}

export async function apiDiscoverRooms() {
  return request<Array<{
    id: string;
    name: string;
    created_by: string;
    member_count: number;
    last_message?: string;
    last_message_time?: number;
    has_password?: number;
  }>>('GET', '/api/rooms/discover');
}

export async function apiCreateRoom(name: string, memberIds?: string[], password?: string) {
  return request<{ id: string; name: string }>(
    'POST', '/api/rooms', { name, member_ids: memberIds, password }
  );
}

export async function apiGetRoom(roomId: string) {
  return request<{ id: string; name: string; members: Array<{ user_id: string }> }>(
    'GET', `/api/rooms/${roomId}`
  );
}

export async function apiJoinRoom(roomId: string, password?: string) {
  return request('POST', `/api/rooms/${roomId}/join`, password ? { password } : undefined);
}

// ---- 메시지 API ----

export async function apiGetMessages(roomId: string, before?: string, limit: number = 50) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (before) params.set('before', before);

  return request<Array<{
    id: string;
    room_id: string;
    sender_id: string;
    sender_name: string;
    encrypted_content: string;
    iv: string;
    type: string;
    created_at: number;
  }>>('GET', `/api/rooms/${roomId}/messages?${params.toString()}`);
}

export async function apiSendMessage(
  roomId: string,
  encryptedContent: string,
  iv: string,
  type: string = 'text',
  replyTo?: string,
  replySender?: string,
  replyPreview?: string
) {
  return request<{ id: string }>(
    'POST', `/api/rooms/${roomId}/messages`,
    { encrypted_content: encryptedContent, iv, type, reply_to: replyTo, reply_sender: replySender, reply_preview: replyPreview }
  );
}

// ---- 사용자 API ----

export async function apiSearchUsers(query: string) {
  return request<Array<{
    id: string;
    username: string;
    display_name: string;
    avatar_color: string;
  }>>('GET', `/api/users/search?q=${encodeURIComponent(query)}`);
}

export async function apiGetOnlineUsers(roomId: string) {
  return request<{ online_count: number }>('GET', `/api/rooms/${roomId}/online`);
}

export async function apiChangeRole(roomId: string, userId: string, role: string) {
  return request('PUT', `/api/rooms/${roomId}/members/${userId}/role`, { role });
}

export async function apiKickMember(roomId: string, userId: string) {
  return request('DELETE', `/api/rooms/${roomId}/members/${userId}`);
}

export async function apiLeaveRoom(roomId: string) {
  return request('DELETE', `/api/rooms/${roomId}/leave`);
}

export async function apiFriendRequest(username: string) {
  return request('POST', '/api/friends/request', { username });
}
export async function apiFriendAccept(username: string) {
  return request('POST', '/api/friends/accept', { username });
}
export async function apiFriendList() {
  return request<Array<{ id: string; username: string; display_name: string; avatar_color: string }>>('GET', '/api/friends');
}
export async function apiFriendRequests() {
  return request<Array<{ id: string; username: string; display_name: string; created_at: number }>>('GET', '/api/friends/requests');
}
export async function apiFriendRemove(username: string) {
  return request('DELETE', `/api/friends/${username}`);
}

export async function apiFriendChat(username: string) {
  return request<{ room_id: string; created: boolean }>('POST', `/api/friends/chat/${username}`);
}

// ---- 설정/데이터 API ----

export async function apiUpdateProfile(displayName: string) {
  return request<{ display_name: string }>('PUT', '/api/auth/profile', { display_name: displayName });
}

export async function apiChangePassword(current: string, newPassword: string) {
  return request('PUT', '/api/auth/password', { current_password: current, new_password: newPassword });
}

export async function apiLogoutAll() {
  return request('DELETE', '/api/auth/sessions');
}

export async function apiExportData() {
  return request<{ messages: unknown[]; rooms: unknown[]; export_time: string }>('GET', '/api/auth/export');
}

export async function apiDeleteData() {
  return request('DELETE', '/api/auth/data');
}

export async function apiDeleteAccount() {
  return request('DELETE', '/api/auth/account');
}
